import React, { useEffect, useState } from 'react';
import { Wand2, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { useImagePromptJob } from '@/hooks/useImagePromptJob';
import { ImageUploader } from '../ImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { StyleSelector } from '../StyleSelector';
import { ImageResultDisplay } from '../ImageResultDisplay';

type FaceSwapMode = 'ai_generate' | 'classic_swap';

export const FaceSwapTab: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  // Mode state
  const [mode, setMode] = useState<FaceSwapMode>('ai_generate');
  const isClassicSwap = mode === 'classic_swap';

  // Form state
  const [faceImage, setFaceImage] = useState('');
  const [facePreviewUrl, setFacePreviewUrl] = useState<string | null>(null);
  const [targetImage, setTargetImage] = useState('');
  const [targetPreviewUrl, setTargetPreviewUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [styles, setStyles] = useState<string[]>([]);
  const [model, setModel] = useState<string | undefined>();

  // Async generation (job + polling) — fixes the old synchronous freeze.
  const { isGenerating, progress, message, result, error, processingTime, run, reset } =
    useImagePromptJob();

  useEffect(() => {
    if (error) {
      showToast(
        'error',
        t('image.notifications.errorTitle', { defaultValue: 'Generation Failed' }),
        error
      );
    }
  }, [error, showToast, t]);

  useEffect(() => {
    if (result && result.length > 0) {
      showToast(
        'success',
        t('image.notifications.successTitle', { defaultValue: 'Images Ready!' }),
        t('image.faceSwap.result.processingTime', {
          defaultValue: 'Generated in {{time}} seconds',
          time: (processingTime || 0).toFixed(1),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const handleFaceSelect = (base64: string, file: File) => {
    setFaceImage(base64);
    const url = URL.createObjectURL(file);
    setFacePreviewUrl(url);
  };

  const handleFaceRemove = () => {
    setFaceImage('');
    setFacePreviewUrl(null);
  };

  const handleTargetSelect = (base64: string, file: File) => {
    setTargetImage(base64);
    const url = URL.createObjectURL(file);
    setTargetPreviewUrl(url);
  };

  const handleTargetRemove = () => {
    setTargetImage('');
    setTargetPreviewUrl(null);
  };

  const handleGenerate = async () => {
    // Validation - face always required
    if (!faceImage) {
      showToast(
        'error',
        t('common.error', { defaultValue: 'Error' }),
        t('image.faceSwap.noFaceWarning', { defaultValue: 'Please upload a face photo' })
      );
      return;
    }

    // Mode-specific validation
    if (isClassicSwap) {
      if (!targetImage) {
        showToast(
          'error',
          t('common.error', { defaultValue: 'Error' }),
          t('image.faceSwap.noTargetWarning', { defaultValue: 'Please upload a target image' })
        );
        return;
      }
    } else if (!prompt.trim()) {
      showToast(
        'error',
        t('common.error', { defaultValue: 'Error' }),
        t('image.faceSwap.noPromptWarning', {
          defaultValue: 'Please describe the target character',
        })
      );
      return;
    }

    const images = isClassicSwap
      ? [
          {
            image: targetImage,
            weight: 1.0,
            stop: 1.0,
            control_type: 'ImagePrompt' as const,
          },
          {
            image: faceImage,
            weight: 0.85,
            stop: 0.85,
            control_type: 'FaceSwap' as const,
          },
        ]
      : [
          {
            image: faceImage,
            weight: 0.85,
            stop: 0.85,
            control_type: 'FaceSwap' as const,
          },
        ];

    await run({
      prompt: isClassicSwap ? '' : prompt,
      images,
      // Styles re-stylize the whole diffusion and fight the target ImagePrompt,
      // so they only apply in AI Generate mode (kept empty for a faithful swap).
      styles: isClassicSwap ? [] : styles,
      model,
      resolution: '1152x896',
    });
  };

  const canGenerate = isClassicSwap ? faceImage && targetImage : faceImage && prompt.trim();

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isGenerating && !result && (
        <>
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t('image.faceSwap.title', { defaultValue: 'Face Swap' })}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {t('image.faceSwap.description', {
                defaultValue: 'Put a face onto a new character or scene',
              })}
            </p>
          </div>

          {/* Checkpoint model selector (reuses the standard Fooocus selector) */}
          <ImageModelSelector
            value={model}
            onChange={setModel}
            provider="fooocus"
            disabled={isGenerating}
          />

          {/* Mode selector */}
          <div>
            <label className="form-label mb-2">
              {t('image.faceSwap.modes.label', { defaultValue: 'Swap Mode' })}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('ai_generate')}
                className={`
                  option-card border-2 p-3 sm:p-4
                  ${mode === 'ai_generate' ? 'border-primary-500 bg-primary-500/20' : 'border-secondary-700 hover:border-primary-500/40'}
                `}
              >
                <Wand2
                  className={`w-6 h-6 mb-2 ${mode === 'ai_generate' ? 'text-primary-400' : 'text-secondary-400'}`}
                />
                <span className="text-sm font-medium text-white">
                  {t('image.faceSwap.modes.aiGenerate', { defaultValue: 'AI Generate' })}
                </span>
                <span className="text-xs text-white/40 mt-1">
                  {t('image.faceSwap.modes.aiGenerateDesc', {
                    defaultValue: 'Face + text prompt to create a new scene',
                  })}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('classic_swap');
                  // Classic Swap preserves the target image — drop any residual
                  // style selection so it can't degrade the swap fidelity.
                  setStyles([]);
                }}
                className={`
                  option-card border-2 p-3 sm:p-4
                  ${mode === 'classic_swap' ? 'border-primary-500 bg-primary-500/20' : 'border-secondary-700 hover:border-primary-500/40'}
                `}
              >
                <ArrowRightLeft
                  className={`w-6 h-6 mb-2 ${mode === 'classic_swap' ? 'text-primary-400' : 'text-secondary-400'}`}
                />
                <span className="text-sm font-medium text-white">
                  {t('image.faceSwap.modes.classicSwap', { defaultValue: 'Classic Swap' })}
                </span>
                <span className="text-xs text-white/40 mt-1">
                  {t('image.faceSwap.modes.classicSwapDesc', {
                    defaultValue: 'Face + target image for direct swap',
                  })}
                </span>
              </button>
            </div>
          </div>

          {/* Face image uploader */}
          <div>
            <label className="form-label mb-2">
              {t('image.faceSwap.uploadFace', { defaultValue: 'Upload Face Photo' })}
            </label>
            <p className="text-xs text-white/40 mb-3">
              {t('image.faceSwap.faceHint', {
                defaultValue: 'Upload a clear photo of the face you want to use',
              })}
            </p>
            <ImageUploader
              onImageSelect={handleFaceSelect}
              onRemove={handleFaceRemove}
              previewUrl={facePreviewUrl || undefined}
              disabled={isGenerating}
              maxSizeMB={10}
            />
          </div>

          {/* Classic mode: target image uploader */}
          {isClassicSwap && (
            <div>
              <label className="form-label mb-2">
                {t('image.faceSwap.uploadTarget', { defaultValue: 'Upload Target Image' })}
              </label>
              <p className="text-xs text-white/40 mb-3">
                {t('image.faceSwap.targetHint', {
                  defaultValue: 'Upload the image where you want to place the face',
                })}
              </p>
              <ImageUploader
                onImageSelect={handleTargetSelect}
                onRemove={handleTargetRemove}
                previewUrl={targetPreviewUrl || undefined}
                disabled={isGenerating}
                maxSizeMB={10}
              />
            </div>
          )}

          {/* AI mode: target character prompt */}
          {!isClassicSwap && (
            <div>
              <label htmlFor="target-prompt" className="form-label mb-2">
                {t('image.faceSwap.prompt', { defaultValue: 'Target Character' })}
              </label>
              <textarea
                id="target-prompt"
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('image.faceSwap.promptPlaceholder', {
                  defaultValue:
                    "Describe the person/scene to put the face on (e.g., 'a astronaut on the moon')",
                })}
                disabled={isGenerating}
                className="input w-full resize-none"
              />
            </div>
          )}

          {/* Style selector — AI Generate only. In Classic Swap styles would
              re-stylize the whole image and pull it away from the target. */}
          {!isClassicSwap && (
            <StyleSelector selectedStyles={styles} onChange={setStyles} disabled={isGenerating} />
          )}

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !canGenerate}
            className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isClassicSwap ? (
              <ArrowRightLeft className="w-5 h-5 mr-2" />
            ) : (
              <Wand2 className="w-5 h-5 mr-2" />
            )}
            {isGenerating
              ? t('image.faceSwap.generating', { defaultValue: 'Generating...' })
              : t('image.faceSwap.generate', { defaultValue: 'Generate' })}
          </button>
        </>
      )}

      {/* Loading state with progress (cold-start aware) */}
      {isGenerating && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
          <p className="text-white/60">
            {message || t('image.faceSwap.generating', { defaultValue: 'Generating...' })}
          </p>
          <p className="mt-2 text-xs text-white/40">
            {t('image.imagePrompt.firstRunNote', {
              defaultValue:
                'The first Image-to-Image run can take a few minutes (one-time setup). Later runs are fast.',
            })}
          </p>
          {progress > 0 && (
            <div className="w-full max-w-md mx-auto mt-4 bg-secondary-700 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && result.length > 0 && !isGenerating && (
        <ImageResultDisplay images={result} processingTime={processingTime || 0} onReset={reset} />
      )}
    </div>
  );
};

export default FaceSwapTab;
